import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGetAllWorkoutQuery } from "@/services/workoutApi";
import { Consultation, HistoryConsultation } from "@/types/consultation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { HistoryFilter } from "../history-filter";
import { Visibility } from "@/types/enums";
import { visibleFor } from "@/lib/constants/workout";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

interface SearchCriteriaType {
    limit: number;
    offset: number;
    sort_key: string;
    sort_order: string;
    search_key?: string;
    [key: string]: string | number | (string | number)[] | null | undefined;
}

const initialValue: SearchCriteriaType = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};

const WorkoutPlanStep = ({ action, open }: FormStepProps) => {
    const { setValue, watch, getValues } = useFormContext<Consultation & { assigned_workout_plan_ids?: number[] }>();
    const navigate = useNavigate();
    const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
    const [hasAutoSelected, setHasAutoSelected] = useState(false);
    
    // Table Data Logic (State for Search & Criteria)
    const {
        searchCriteria,
        setSearchCriteria,
        query,
        inputValue,
        setInputValue,
    } = useTableData<SearchCriteriaType>(initialValue);

    // Fetch Workout Plans
    const { data: workoutsData, isLoading } = useGetAllWorkoutQuery({ org_id: Number(orgId), query: query }, { skip: !open });
    
    // Selection State
    const assignedIds = watch("assigned_workout_plan_ids") || [];
    const memberId = watch("member_id");
    
    // For workout plans, we check if the current member is already in the 'members' array of the workout
    useEffect(() => {
        if (!isLoading && workoutsData?.data && !hasAutoSelected && memberId) {
            const preSelectedIds: number[] = [];
            workoutsData.data.forEach(plan => {
                const planId = Number(plan.id);
                const memberIds = plan.members?.map((m: any) => typeof m === 'number' ? m : m.member_id) || [];
                
                if (planId && !Number.isNaN(planId) && memberIds.includes(Number(memberId))) {
                    preSelectedIds.push(planId);
                }
            });

            if (preSelectedIds.length > 0) {
                 if (assignedIds.length === 0) {
                     setValue("assigned_workout_plan_ids", preSelectedIds, { shouldValidate: true, shouldDirty: true });
                 }
            }
            if (workoutsData.data.length > 0) {
                setHasAutoSelected(true);
            }
        }
    }, [isLoading, workoutsData, memberId, hasAutoSelected, setValue, assignedIds.length]);


    const handleSelect = (planId: number) => {
        if (!planId) return;

        const currentIds = getValues("assigned_workout_plan_ids") || [];
        let newIds = [...currentIds];
        
        if (newIds.includes(planId)) {
            newIds = newIds.filter(id => id !== planId);
        } else {
            newIds.push(planId);
        }
        
        newIds = Array.from(new Set(newIds)).filter(id => typeof id === 'number' && !Number.isNaN(id));
        setValue("assigned_workout_plan_ids", newIds, { shouldValidate: true, shouldDirty: true });
    };

    const getVisibilityLabel = (value: string) => {
        return visibleFor.find(v => v.value === value)?.label || value;
    };
    
    // Pagination Hooks
    const totalRecords = workoutsData?.filtered_counts || 0;
    const {
        handleLimitChange,
        handleNextPage,
        handlePrevPage,
        handleFirstPage,
        handleLastPage,
        isLastPage,
    } = usePagination<SearchCriteriaType>({
        totalRecords,
        searchCriteria,
        setSearchCriteria,
    });

    const [historyData, setHistoryData] = useState<HistoryConsultation | null>(null);

    return (
        <div className="space-y-4 h-full flex flex-col">
            <HistoryFilter 
                memberId={memberId!} 
                onDataFound={setHistoryData} 
            />

            {historyData && historyData.workout_plans && historyData.workout_plans.length > 0 && (
                <Card className="p-4 border-[#e2e8f0]">
                    <div className="flex items-center justify-between mb-3">
                        <p className="font-semibold text-sm text-[#1e293b]">Previous Workout Plans</p>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 uppercase">
                            Historical View
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {historyData.workout_plans.map((plan) => (
                            <Badge key={plan.id} variant="outline" className="bg-[#f8fafc] border-[#f1f5f9] text-[#1e293b] font-medium py-1">
                                {plan.workout_name}
                            </Badge>
                        ))}
                    </div>
                </Card>
            )}

            <Card className="p-4 flex flex-col h-full bg-white">
            <div className="space-y-4 h-full flex flex-col">
                <div className="flex items-center gap-4 shrink-0">
                 <TableSearchFilter<SearchCriteriaType>
                    setSearchCriteria={setSearchCriteria}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    placeHolder="Search Workouts"
                  />
                  <Button 
                    type="button" 
                    className="bg-primary text-sm text-black flex items-center gap-1 lg:mb-0 h-8"
                    onClick={() => navigate("/admin/workoutplans/add/step/1?mode=add")}
                  >
                    <PlusIcon className="size-4" />
                    <span>Create New</span>
                  </Button>
            </div>

            <div className="rounded-none flex-1 flex flex-col">
                <ScrollArea className="flex-1 min-h-[400px]">
                    {isLoading ? (
                        <div className="flex justify-center p-8 text-gray-400">Loading workout plans...</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-1">
                            {workoutsData?.data?.map((plan) => {
                             const planId = Number(plan.id);

                             if (!planId || Number.isNaN(planId)) return null; 

                            const isSelected = assignedIds.includes(planId);
                            
                            return (
                            <Card 
                                key={planId} 
                                className={cn(
                                    "cursor-pointer transition-all hover:border-primary/50",
                                    isSelected ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-gray-200"
                                )}
                                onClick={() => handleSelect(planId)}
                            >
                                <CardContent className="p-4 flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                         {/* Image */}
                                         {plan.image_url ? (
                                            <img src={plan.image_url} alt={plan.workout_name} className="w-12 h-12 rounded-full object-cover bg-gray-100" />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                                                No Img
                                            </div>
                                        )}
                                        
                                        {/* Details */}
                                        <div className="flex flex-col">
                                            <h4 className="font-semibold text-sm text-gray-900 truncate max-w-[150px]">{plan.workout_name}</h4>
                                            <p className="text-[10px] text-gray-500">
                                                {plan.level} • {plan.weeks} Weeks
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* Visibility Badge */}
                                    <Badge 
                                        variant="secondary" 
                                        className={cn(
                                            "text-[10px] font-normal whitespace-nowrap px-2 py-0.5 h-6",
                                            plan.visible_for === Visibility.EveryoneInMyClub 
                                                ? "bg-green-100 text-green-700" 
                                                : "bg-blue-100 text-blue-700"
                                        )}
                                    >
                                        {getVisibilityLabel(plan.visible_for || "")}
                                    </Badge>
                                </CardContent>
                            </Card>
                        )})}
                        {(!workoutsData?.data || workoutsData.data.length === 0) && (
                            <div className="col-span-full text-center p-8 text-gray-400 text-sm">
                                No workout plans found.
                            </div>
                        )}
                    </div>
                )}
            </ScrollArea>
            </div>
            
            {/* Pagination Controls */}
             {(workoutsData?.filtered_counts || 0) > 0 && (
                <div className="shrink-0 pt-2 border-t mt-auto">
                    <Pagination
                      limit={searchCriteria.limit}
                      offset={searchCriteria.offset}
                      totalItems={totalRecords}
                      onLimitChange={handleLimitChange}
                      onNextPage={handleNextPage}
                      onPrevPage={handlePrevPage}
                      onFirstPage={handleFirstPage}
                      onLastPage={handleLastPage}
                      isLastPage={isLastPage}
                    />
                </div>
              )}
            </div>
        </Card>
        </div>
    );
};

export default WorkoutPlanStep;
